/**
 * TransactionColumnRegistry
 *
 * Defines standard transaction table columns with fixed semantic identity.
 * Columns have stable keys, default labels, format hints, and pure accessor functions.
 * Client config overrides labels and visibility — the key always means the same thing.
 *
 * Configuration via clientData.transaction_columns_config:
 *   {
 *     strikePrice: { visible: true, label: "Cost Basis" },
 *     taxBasis: { visible: true },
 *     sharePrice: { label: "Gift FMV" },
 *     referencePrice: { visible: true },  // show in table (normally export-only)
 *   }
 *
 * Falls back to legacy flags/language when transaction_columns_config is absent.
 *
 * Custom (per-client, non-built-in) columns via clientData.transaction_custom_columns
 * -- shape and philosophy deliberately mirror Cap Table Custom Columns (CTCC,
 * see docs/cap-table-custom-columns.md and transaction-custom-column-value.js
 * for the full explanation of what's shared with CTCC and what's deliberately
 * not, namely storage location). Multiple custom columns are supported per
 * client (2026-08-21) -- each gets its own stable, derived key (see
 * deriveCustomColumnKey below), mirroring cap table's key derivation rather
 * than a fixed system name like the old single-slot "customColumn" (BW's
 * pre-existing column keeps that literal key -- it's just an ordinary key now,
 * not special-cased):
 *   transaction_custom_columns: [
 *     { key: "gift_fmv", label: "Gift FMV", source: "custom", format: "currency",
 *       includeInExport: true, showInTableUI: true },
 *     { key: "special_note", label: "Special Note", source: "custom", format: "text",
 *       includeInExport: true, showInTableUI: true },
 *   ]
 * These merge into this registry's column list alongside STANDARD_COLUMNS below,
 * so a custom column participates in getVisibleColumns()/getExportColumns()/
 * getLabel() etc. exactly like a built-in one. See
 * transaction-custom-column-value.js's buildTransactionCustomColumnDefinitions().
 * Relabeling isn't just a display change -- for EXTERNAL_DATA (CSV-backed)
 * clients, the physical CSV header itself is renamed to match (see
 * TransactionService.js's renameExternalCustomColumnHeaders()), since the
 * column is recognized on read/write by matching its current label, not a
 * fixed system name.
 *
 * Usage:
 *   const registry = new TransactionColumnRegistry(clientData);
 *   const columns = registry.getVisibleColumns({ isConsolidatedView: true });
 *   columns.forEach(col => {
 *     const label = registry.getLabel(col.key);
 *     const value = col.accessor(transaction);
 *   });
 */

import { normalizeDateString } from './date-utils.js';
import { buildTransactionCustomColumnDefinitions, listTransactionCustomColumns } from './transaction-custom-column-value.js';
import { resolveHeaderMap, normalizeHeader, isReadOnlyHeader } from '../services/TransactionService.js';

function parseNum(val) {
	if (val == null || val === '') return null;
	if (typeof val === 'number') return isNaN(val) ? null : val;
	const cleaned = String(val).replace(/[^-.\d]/g, '');
	const num = Number(cleaned);
	return isNaN(num) ? null : num;
}

/**
 * Build a transaction_columns_config from legacy clientData flags and language.
 * Used when clientData.transaction_columns_config is not set.
 */
function buildConfigFromLegacy(clientData) {
	const config = {};
	const flags = clientData.flags || {};
	const language = clientData.language || {};

	// Label overrides from language. Populate BOTH Ledgers' original keys
	// (sharePrice/strikePrice) and Events & Transactions' independent keys
	// (sharePriceET/strikePriceET) identically here (2026-08-04) -- see the
	// "*ET" STANDARD_COLUMNS entries below for why these are separate,
	// explicitly-configurable columns rather than one shared setting. Before
	// either surface had its own settings UI, both were always driven by
	// these same legacy flags/language values, so this preserves that identical
	// default behavior for every client that's never touched either surface's
	// new column settings -- divergence only happens once an admin explicitly
	// configures ONE surface via its own modal (see ledgers.js's
	// saveTxnColumnSettings and shared_assets/index.js's renderETColumnSettings,
	// each of which only ever writes its own surface's keys). taxBasis/
	// taxBasisET's label is applied later, gated behind flags.show_tax_basis_column
	// -- see #6263.
	if (language.label_cost_price) {
		config.sharePrice = { ...config.sharePrice, label: language.label_cost_price };
		config.sharePriceET = { ...config.sharePriceET, label: language.label_cost_price };
	}
	if (language.label_strike_price) {
		config.strikePrice = { ...config.strikePrice, label: language.label_strike_price };
		config.strikePriceET = { ...config.strikePriceET, label: language.label_strike_price };
	}

	// Label overrides from language flags
	if (language.label_investments) {
		config.shareClass = { label: language.label_investments };
	} else if (clientData.use_real_estate_language) {
		config.shareClass = { label: 'Property' };
		config.quantity = { label: 'Ownership' };
	} else if (clientData.use_fund_language) {
		config.shareClass = { label: 'Fund' };
		config.quantity = { label: 'Contribution' };
	}

	// Visibility from legacy flags -- again populating both the Ledgers and
	// Events & Transactions keys identically.
	if (flags.use_tax_basis) {
		// Legacy behavior: use_tax_basis showed strikePrice column with the tax basis label
		const strikeLabelFromTaxBasis = language.label_tax_basis || 'Tax Basis';
		config.strikePrice = { ...config.strikePrice, visible: true, label: config.strikePrice?.label || strikeLabelFromTaxBasis };
		config.strikePriceET = { ...config.strikePriceET, visible: true, label: config.strikePriceET?.label || strikeLabelFromTaxBasis };
	}
	if (flags.show_tax_basis_column) {
		// #6263: only relabel taxBasis/taxBasisET when this flag is actually turning them on -- was unconditional, colliding with use_tax_basis's own "Threshold"-style label above.
		const taxBasisLabelFromFlag = language.label_tax_basis || 'Tax Basis';
		config.taxBasis = { ...config.taxBasis, visible: true, label: config.taxBasis?.label || taxBasisLabelFromFlag };
		config.taxBasisET = { ...config.taxBasisET, visible: true, label: config.taxBasisET?.label || taxBasisLabelFromFlag };
	}

	// Re-added 2026-08-04: removed 2026-08-03 as dead-for-Ledgers code (its
	// accessor always returned null there, and this registry wasn't consumed
	// by anything else). Now that Events & Transactions has its own settings
	// UI backed by this same registry (see shared_assets/index.js's
	// setLabels()), 'balance' is real again -- E&T computes its own value via
	// the balanceHistory/directionForUser walk in renderUserTransactionsModal,
	// this just controls whether that column shows. Legacy fallback preserves
	// existing behavior for any client that's never touched the new settings
	// UI (e.g. Marvin, via flags.show_ET_balances). Unlike sharePrice/
	// strikePrice/taxBasis, 'balance' was already Events & Transactions-only
	// from the start, so it doesn't need a separate Ledgers/ET key pair.
	if (flags.show_ET_balances) config.balance = { ...config.balance, visible: true };

	return config;
}

export const STANDARD_COLUMNS = [
	{
		key: 'shareClass',
		defaultLabel: 'Class',
		format: 'text',
		accessor: (txn) => txn.shareClass,
		visibility: 'always',
		visibilityRule: null,
		includeInExport: true,
	},
	{
		key: 'holder',
		defaultLabel: 'Account',
		format: 'text',
		accessor: (txn) => txn.holderName || txn.holder || '',
		visibility: 'conditional',
		visibilityRule: (config) => !!config._isConsolidatedView,
		includeInExport: false,
	},
	{
		key: 'date',
		defaultLabel: 'Date',
		format: 'date',
		accessor: (txn) => txn.date,
		visibility: 'always',
		visibilityRule: null,
		includeInExport: true,
	},
	{
		key: 'securityType',
		defaultLabel: 'Security Type',
		format: 'text',
		accessor: (txn) => txn.securityType || '',
		visibility: 'conditional',
		visibilityRule: () => false,
		includeInExport: true,
	},
	{
		key: 'certNumber',
		defaultLabel: 'Cert #',
		format: 'text',
		accessor: (txn) => txn.certNumber || '',
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.certNumber?.visible,
		// Export mirrors table visibility (2026-08-26, per Chris): Cert #
		// exports whenever it's already showing in the table (ledgers.js's
		// runtime-only hasCertNumbers check sets _colConfig.certNumber.visible
		// for exactly that case) -- getExportColumns()'s visibilityRule branch
		// already covers this, so no separate opt-in is needed. Previously
		// hard-excluded from export via neverExport (#5371, "suppress cert/
		// account from non-applicable exports") regardless of visibility;
		// removed neverExport here so Cert # follows the same visible-in-
		// table-means-in-export rule as every other conditional column.
		includeInExport: false,
	},
	{
		key: 'description',
		defaultLabel: 'Description',
		format: 'text',
		accessor: (txn) => txn.notes || '',
		visibility: 'always',
		visibilityRule: null,
		includeInExport: true,
	},
	{
		key: 'nrid',
		defaultLabel: 'nrid',
		format: 'text',
		accessor: (txn) => txn.nrid || '',
		visibility: 'conditional',
		visibilityRule: () => false,
		includeInExport: true,
	},
	{
		key: 'sender',
		defaultLabel: 'Sender',
		format: 'text',
		accessor: (txn) => txn.senderName || 'Issuer',
		visibility: 'always',
		visibilityRule: null,
		includeInExport: true,
	},
	{
		key: 'senderEmail',
		defaultLabel: 'Sender Email',
		format: 'text',
		accessor: (txn) => txn.sender || '',
		visibility: 'conditional',
		visibilityRule: () => false,
		includeInExport: true,
	},
	{
		key: 'receiver',
		defaultLabel: 'Receiver',
		format: 'text',
		accessor: (txn) => txn.receiverName || 'Issuer',
		visibility: 'always',
		visibilityRule: null,
		includeInExport: true,
	},
	{
		key: 'receiverEmail',
		defaultLabel: 'Receiver Email',
		format: 'text',
		accessor: (txn) => txn.receiver || '',
		visibility: 'conditional',
		visibilityRule: () => false,
		includeInExport: true,
	},
	{
		key: 'quantity',
		defaultLabel: 'Quantity',
		format: 'number',
		accessor: (txn) => txn.quantity,
		visibility: 'always',
		visibilityRule: null,
		includeInExport: true,
	},
	{
		key: 'sharePrice',
		defaultLabel: 'Price',
		format: 'currency',
		accessor: (txn) => txn.sharePrice,
		// Changed 2026-08-03: was visibility: 'always' (locked on, no opt-out).
		// Chris wants Share Price hideable per-client, defaulting to visible for
		// every existing client that hasn't said otherwise -- so this reads
		// "hidden only if explicitly set to false", not "visible only if
		// explicitly set to true" like every other conditional column here.
		// See ledgers.js's ALL_STANDARD_COLUMNS (now locked: false, defaultVisible:
		// true) and its saveTxnColumnSettings handler, which had to learn to
		// write visible:false explicitly when this one gets turned off --
		// otherwise omitting the key from the saved snapshot would fall right
		// back to visible on the next load.
		visibility: 'conditional',
		visibilityRule: (config) => config._colConfig.sharePrice?.visible !== false,
		includeInExport: true,
	},
	{
		key: 'exactPrice',
		defaultLabel: 'Exact Price',
		format: 'currency',
		accessor: (txn) => txn.sharePrice,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.exactPrice?.visible,
		includeInExport: true,
	},
	{
		key: 'referencePrice',
		defaultLabel: 'Reference Price',
		format: 'currency',
		accessor: (txn) => txn.referencePrice,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.referencePrice?.visible,
		includeInExport: true,
	},
	{
		key: 'qtyTimesPrice',
		defaultLabel: 'Qty x Price',
		format: 'currency',
		accessor: (txn) => {
			const qty = parseNum(txn.quantity);
			const price = parseNum(txn.sharePrice);
			return qty != null && price != null ? qty * price : null;
		},
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.qtyTimesPrice?.visible,
		includeInExport: true,
	},
	{
		key: 'qtyTimesRefPrice',
		defaultLabel: 'Qty x Ref. Price',
		format: 'currency',
		accessor: (txn) => {
			const qty = parseNum(txn.quantity);
			const refPrice = parseNum(txn.referencePrice);
			return qty != null && refPrice != null ? qty * refPrice : null;
		},
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.qtyTimesRefPrice?.visible,
		includeInExport: true,
	},
	{
		key: 'balance',
		defaultLabel: 'Balance',
		format: 'number',
		// Re-added 2026-08-04 (see buildConfigFromLegacy's show_ET_balances
		// comment above for the full history). This accessor is intentionally
		// a stub -- unlike every other column here, a running balance can't be
		// computed from a single transaction in isolation; it requires walking
		// the user's full transaction history in date order. Events &
		// Transactions computes the real value itself (balanceHistory /
		// directionForUser in shared_assets/index.js's renderUserTransactionsModal)
		// and only asks this registry whether the column should show and what
		// to call it. Ledgers has no such per-account walk (it lists every
		// holder's transactions together) and explicitly forces this column
		// invisible at render time regardless of what this resolves to -- see
		// ledgers.js's runtimeTransactionColumnsConfig.balance override.
		accessor: () => null,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.balance?.visible,
		includeInExport: false,
	},
	{
		key: 'strikePrice',
		defaultLabel: 'Strike Price',
		format: 'currency',
		accessor: (txn) => txn.strikePrice,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.strikePrice?.visible,
		includeInExport: true,
	},
	{
		key: 'expDate',
		defaultLabel: 'Expiration Date',
		format: 'date',
		// Fixed 2026-07-31, two passes. First pass: this threw RangeError:
		// Invalid time value (uncaught, crashing the whole Ledgers table
		// render) whenever txn.expDate was truthy but not a clean unix-seconds
		// value -- Number(txn.expDate) went to NaN, new Date(NaN) is a valid
		// "Invalid Date" object, and .toISOString() throws on it. Since expDate
		// is export-only (visibilityRule below is always false),
		// formatExportCell() calls this accessor unconditionally for every
		// transaction row, so a single bad value took down the entire table.
		// Second pass: the crash-safe version fell back to '' for anything that
		// wasn't a clean unix-seconds number -- but real data has expDate
		// stored as human-formatted strings too (e.g. "Aug 16 2024", confirmed
		// directly from an export), so that "safe" version was silently
		// blanking out real expiration dates instead of showing them. Routed
		// through normalizeDateString() (date-utils.js), which already handles
		// both shapes -- unix-seconds numbers and the same dateFormats list
		// (number-formats.js) used everywhere else in this app for loosely-
		// formatted date strings, "MMM D YYYY" included -- and never throws,
		// returning '' only for genuinely unparseable values.
		accessor: (txn) => normalizeDateString(txn.expDate, true),
		visibility: 'conditional',
		visibilityRule: () => false,
		includeInExport: true,
	},
	{
		key: 'taxBasis',
		defaultLabel: 'Tax Basis',
		format: 'currency',
		accessor: (txn) => txn.taxBasis,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.taxBasis?.visible,
		includeInExport: true,
	},
	// Note: a client's custom columns (however many -- e.g. BW's "Gift FMV")
	// are NOT hardcoded entries here -- each is a transaction_custom_columns
	// entry (see this file's top docstring and transaction-custom-column-
	// value.js), merged into this registry's column list at construction
	// time. They're inline-editable in Ledgers and Events & Transactions only
	// (see transaction-table-renderer.js's col._isCustom branch,
	// ledgers.js's renderLedgerCustomColumnListeners, and shared_assets/
	// index.js's renderEventsTransCustomColumnListeners) -- the +New/Edit
	// transaction modals don't capture them, by design; admins fill them in
	// via inline edit after the transaction is created.

	// ── Events & Transactions-only variants (2026-08-04) ──────────────────
	// Ledgers (an admin, all-holders-at-once view) and Events & Transactions
	// (a shareholder-facing, per-account view) both let an admin configure
	// Share Price / Strike Price / Tax Basis -- but per Chris, the two
	// audiences are different enough that the two surfaces shouldn't be
	// forced to always show/label these identically. These *ET columns are
	// otherwise identical twins of sharePrice/strikePrice/taxBasis above --
	// same accessors, same legacy-flag defaults (see buildConfigFromLegacy,
	// which populates both key pairs together) -- just resolved from their
	// own, independent transaction_columns_config keys. Ledgers' settings UI
	// (ledgers.js) only ever reads/writes the plain keys above; Events &
	// Transactions' settings UI (shared_assets/index.js's ET_STANDARD_COLUMNS)
	// only ever reads/writes these. Until an admin explicitly saves either
	// surface's settings, both resolve identically via the shared legacy
	// fallback, so nothing changes for any client that hasn't touched either.
	{
		key: 'sharePriceET',
		defaultLabel: 'Price',
		format: 'currency',
		accessor: (txn) => txn.sharePrice,
		visibility: 'conditional',
		visibilityRule: (config) => config._colConfig.sharePriceET?.visible !== false,
		includeInExport: false,
	},
	{
		key: 'strikePriceET',
		defaultLabel: 'Strike Price',
		format: 'currency',
		accessor: (txn) => txn.strikePrice,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.strikePriceET?.visible,
		includeInExport: false,
	},
	{
		key: 'taxBasisET',
		defaultLabel: 'Tax Basis',
		format: 'currency',
		accessor: (txn) => txn.taxBasis,
		visibility: 'conditional',
		visibilityRule: (config) => !!config._colConfig.taxBasisET?.visible,
		includeInExport: false,
	},
];

// Stored transaction fields, always part of the full Transactions export (see getFullExportColumns).
const FULL_EXPORT_STANDARD_KEYS = new Set([
	'shareClass',
	'date',
	'securityType',
	'certNumber',
	'description',
	'nrid',
	'sender',
	'senderEmail',
	'receiver',
	'receiverEmail',
	'quantity',
	'sharePrice',
	'strikePrice',
	'expDate',
	'taxBasis',
]);

export class TransactionColumnRegistry {
	constructor(clientData = {}) {
		// #6123 (Sentry): default param above only covers `undefined` -- a caller
		// racing ahead of data load can pass an explicit `null`, which crashed
		// buildConfigFromLegacy's `clientData.flags` below. Normalize here so
		// the rest of this constructor can assume a real object.
		clientData = clientData || {};
		// Resolve column config as a PER-COLUMN merge (2026-08-04), not an
		// all-or-nothing choice between "explicit config" and "legacy flags".
		// Previously this was `clientData.transaction_columns_config ||
		// buildConfigFromLegacy(clientData)` -- the moment a client had ANY
		// explicit transaction_columns_config saved (from either Ledgers or
		// Events & Transactions), the ENTIRE legacy fallback stopped applying,
		// even for columns the saved config never mentioned. That was the
		// documented root cause of the original Cost Basis relabeling
		// confusion, and it would have silently broken Events & Transactions'
		// Balance column too: a client with flags.show_ET_balances still true
		// but an unrelated transaction_columns_config already saved (e.g. from
		// Ledgers hiding Share Price) would lose Balance entirely, since
		// nothing in that saved config ever mentioned 'balance'. Per Chris:
		// legacy flags/settings should keep winning per-column until an admin
		// actually configures that specific column via the new settings UI.
		// Per-client column list: built-in STANDARD_COLUMNS plus this client's
		// transaction_custom_columns (2026-08-05, see this file's top docstring
		// and transaction-custom-column-value.js). Custom columns are appended,
		// never prepended/inserted -- canonical column order for built-ins is
		// unaffected, matching every consumer's assumption (e.g. the CSV export
		// scraper's reliance on STANDARD_COLUMNS' fixed order) that a client's
		// custom columns land in whatever position they're declared, at the end.
		this._columns = STANDARD_COLUMNS.concat(buildTransactionCustomColumnDefinitions(clientData));

		const legacyConfig = buildConfigFromLegacy(clientData);
		const explicitConfig = clientData.transaction_columns_config || {};
		this._colConfig = {};
		this._columns.forEach((col) => {
			const legacyVal = legacyConfig[col.key];
			const explicitVal = explicitConfig[col.key];
			if (legacyVal || explicitVal) {
				this._colConfig[col.key] = { ...legacyVal, ...explicitVal };
			}
		});
		this._columnMap = new Map();
		this._labels = new Map();

		// Build lookup map
		this._columns.forEach((col) => {
			this._columnMap.set(col.key, col);
		});

		// Resolve labels: config label > default label
		this._columns.forEach((col) => {
			const colConf = this._colConfig[col.key];
			const label = colConf?.label || col.defaultLabel;
			this._labels.set(col.key, label);
		});
	}

	getLabel(key) {
		return this._labels.get(key);
	}

	/**
	 * Override a single column's resolved label dynamically. Used by callers
	 * that need to inject a per-class label (#5298 / U5) — the registry is
	 * built client-wide, so per-class context arrives after construction.
	 * @param {string} key - Column key (e.g., 'quantity')
	 * @param {string|null|undefined} label - Override label; falsy values restore the original
	 */
	setLabel(key, label) {
		if (!this._columnMap.has(key)) return;
		if (label) {
			this._labels.set(key, label);
		} else {
			// Restore the default label from config / column default.
			const col = this._columnMap.get(key);
			const colConf = this._colConfig[key];
			this._labels.set(key, colConf?.label || col.defaultLabel);
		}
	}

	getColumn(key) {
		return this._columnMap.get(key);
	}

	getAllColumns() {
		return this._columns.slice();
	}

	/**
	 * Returns columns that should be visible in the table UI.
	 */
	getVisibleColumns(context = {}) {
		const merged = this._mergeContext(context);
		return this._columns.filter((col) => {
			if (col.visibility === 'always') return true;
			if (col.visibility === 'conditional') return col.visibilityRule(merged);
			return false;
		});
	}

	/**
	 * Returns columns for export: all visible columns + any column with includeInExport.
	 * Config can opt a column out ({ includeInExport: false }) or in ({ includeInExport: true }).
	 */
	getExportColumns(context = {}) {
		const merged = this._mergeContext(context);
		return this._columns.filter((col) => {
			// Hard exclusion — visible in table but never in CSV export
			if (col.neverExport) return false;
			const colConf = this._colConfig[col.key];
			// Client config override: false opts out, true (#6275) includes even a hidden column
			if (colConf?.includeInExport === false) return false;
			if (colConf?.includeInExport === true) return true;
			// Include if visible
			if (col.visibility === 'always') return true;
			if (col.visibility === 'conditional' && col.visibilityRule(merged)) return true;
			// Include if column default says export even when not visible
			if (col.includeInExport) return true;
			return false;
		});
	}

	/**
	 * Columns for the Ledgers Transactions export: every stored field, shown or not, plus each custom
	 * column -- minus any the admin set to don't-export. Derived columns (Qty x Price, Balance, ...) are
	 * left out, and Cert # only appears when the client uses it (its runtime visibility).
	 */
	getFullExportColumns() {
		return this._columns.filter((col) => {
			const colConf = this._colConfig[col.key];
			if (col.neverExport || colConf?.includeInExport === false) return false;
			if (col._isCustom) return true;
			if (col.key === 'certNumber') return !!colConf?.visible;
			return FULL_EXPORT_STANDARD_KEYS.has(col.key);
		});
	}

	/** Whether `key` is a real column that can appear in an export at all. */
	canExport(key) {
		const col = this._columns.find((c) => c.key === key);
		return !!col && !col.neverExport;
	}

	/** Whether `key` is exported with no config override, given whether it's visible (#6275). */
	isExportedByDefault(key, isVisible) {
		const col = this._columns.find((c) => c.key === key);
		return !!col && !col.neverExport && (isVisible || !!col.includeInExport);
	}

	_mergeContext(context) {
		return {
			_colConfig: this._colConfig,
			_isConsolidatedView: !!context.isConsolidatedView,
		};
	}
}

// ─────────────────────────────────────────────────────────────────────────
// Shared column-settings helpers. The settings panel itself (both Ledgers and
// Events & Transactions) is transaction-column-settings.js; each page still
// owns its column list, DOM containers and save handler.
// ─────────────────────────────────────────────────────────────────────────

/**
 * Returns the key of another column already using `label`, or null. Compared the way CSV headers
 * are matched (case and punctuation ignored, #6405), so "Gift FMV" and "Gift-FMV" conflict. Mirrors users.js's isCapTableCustomColumnLabelAllowed --
 * that check never got ported here, which is how e.g. Asplundh ended up with
 * both sharePrice and taxBasis labeled "Transaction Cost".
 */
export function findColumnLabelConflict(label, excludeKey, labelEdits) {
	const norm = normalizeHeader(label);
	if (!norm) return null;
	for (const key in labelEdits) {
		if (key === excludeKey) continue;
		if (normalizeHeader(labelEdits[key]) === norm) return key;
	}
	return null;
}

// Placeholder key older builds stored in transaction_columns_config; cleaned out on save
export const LEGACY_CUSTOM_PLACEHOLDER_KEY = '__addCustomColumn';

const NEW_CUSTOM_KEY_PREFIX = '__newCustom_';

/**
 * A custom column added in the settings modal but not saved yet. Its temporary key is replaced
 * by one derived from the label in buildCustomColumnUpdate (see deriveCustomColumnKey).
 */
export function createPendingCustomColumn(seq, { label, format, showInTableUI, includeInExport }) {
	return {
		key: NEW_CUSTOM_KEY_PREFIX + seq,
		defaultLabel: label,
		locked: false,
		defaultVisible: showInTableUI !== false,
		format: format || 'text',
		includeInExport: includeInExport !== false,
		_isCustomSlot: true,
		_isNewCustom: true,
	};
}

/**
 * Derive a stable custom-column key from its label -- mirrors cap table's
 * own key derivation (src/admin/users/users.js's manual-column "baseKey"
 * logic): a lowercase, underscored slug of the label, de-duped against
 * existingKeys with a numeric suffix if it collides. BW's pre-existing
 * literal "customColumn" key is left alone by callers -- this is only used
 * for columns created from here on.
 */
export function deriveCustomColumnKey(label, existingKeys) {
	const baseKey =
		String(label || '')
			.toLowerCase()
			.replace(/\s+/g, '_')
			.replace(/[^a-z0-9_]/g, '') || 'custom';
	let key = baseKey;
	let n = 1;
	while (existingKeys.has(key)) {
		key = baseKey + '_' + n;
		n++;
	}
	return key;
}

// #6275: keys a custom column can't take -- the backend never overwrites a field already on the
// transaction, so a clashing value is silently dropped (compared lowercase; keys are lowercase slugs)
const RESERVED_CUSTOM_COLUMN_KEYS = new Set(
	STANDARD_COLUMNS.map((c) => c.key)
		.concat(['notes', 'senderName', 'receiverName', 'certSort', 'formattedDate', 'formattedExpDate', 'customColumns'])
		.map((k) => k.toLowerCase())
);

/**
 * Why `label` can't name a custom column, or null. Rejects a label whose derived key is a built-in
 * field name, or that matches a built-in CSV header (external-data clients would share that column).
 */
export function getCustomColumnLabelProblem(label) {
	const trimmed = String(label || '').trim();
	if (!trimmed) return null;
	if (RESERVED_CUSTOM_COLUMN_KEYS.has(deriveCustomColumnKey(trimmed, new Set()))) {
		return `"${trimmed}" is a built-in transaction field name -- give this custom column a different name.`;
	}
	if (Object.keys(resolveHeaderMap([trimmed])).length) {
		return `"${trimmed}" is already a built-in transaction column -- give this custom column a different name.`;
	}
	// #6405: read-only headers are stripped from the CSV on every load, taking the column's values with them
	if (isReadOnlyHeader(trimmed)) {
		return `"${trimmed}" is a read-only transaction column -- give this custom column a different name.`;
	}
	return null;
}

/**
 * Labels used by more than one custom column (compared the way CSV headers are matched). Such
 * columns share one CSV header, so only the first can ever hold values.
 */
export function findDuplicateCustomColumnLabels(clientData) {
	const seen = new Map();
	const duplicates = [];
	for (const col of listTransactionCustomColumns(clientData)) {
		const norm = normalizeHeader(col.label || col.key);
		if (!norm) continue;
		if (seen.has(norm)) {
			if (!duplicates.includes(seen.get(norm))) duplicates.push(seen.get(norm));
		} else {
			seen.set(norm, col.label || col.key);
		}
	}
	return duplicates;
}

/**
 * The key a not-yet-saved custom column will get, derived the way buildCustomColumnUpdate does:
 * after the existing custom columns and any pending ones added before it (untilKey stops at that one).
 */
export function previewCustomColumnKey(label, clientData, pendingColumns, labelEdits, untilKey) {
	const taken = new Set(listTransactionCustomColumns(clientData).map((c) => c.key));
	for (const pending of pendingColumns) {
		if (pending.key === untilKey) break;
		taken.add(deriveCustomColumnKey(String(labelEdits[pending.key] || pending.defaultLabel).trim(), taken));
	}
	return deriveCustomColumnKey(label, taken);
}

/**
 * Builds the transaction_custom_columns update (creation/relabel/removal,
 * for any number of custom columns) from a settings modal's current state --
 * transaction_custom_columns is a SEPARATE clientData field from
 * transaction_columns_config, where each custom column's actual existence
 * (key/label/source/format) gets defined, not just its visibility/label override.
 *
 * columnDefs holds one entry per already-defined custom column plus any columns added in the
 * modal but not saved yet (col._isNewCustom, see createPendingCustomColumn); every entry is
 * marked col._isCustomSlot. An existing column that's hidden keeps its definition
 * (showInTableUI: false) rather than being deleted.
 *
 * state.formatEdits: {key: 'text'|'currency'|'number'|'date'}; state.exportEdits: {key: boolean}.
 * Both fall back to the existing column's value, then 'text' / true.
 *
 * @returns {{ customColumnsUpdate: object[]|null, renames: {oldLabel: string, newLabel: string}[], problems: string[] }}
 *   problems: reasons the caller must refuse the save (see getCustomColumnLabelProblem)
 */
export function buildCustomColumnUpdate({ clientData, columnDefs, state }) {
	const existingByKey = new Map(listTransactionCustomColumns(clientData).map((c) => [c.key, c]));
	const existingKeys = new Set(existingByKey.keys());
	const customColumnsUpdate = [];
	const renames = [];
	const problems = [];

	columnDefs.forEach((col) => {
		if (!col._isCustomSlot) return;

		const wantsIt = state.visibleKeys.has(col.key);
		const label = (state.labelEdits[col.key] || '').trim();
		const existing = existingByKey.get(col.key);

		if (col._isNewCustom) {
			if (!label) return;
			const problem = getCustomColumnLabelProblem(label);
			if (problem) {
				problems.push(problem);
				return;
			}
			const key = deriveCustomColumnKey(label, existingKeys);
			existingKeys.add(key);
			customColumnsUpdate.push({
				key,
				label,
				source: 'custom',
				format: state.formatEdits?.[col.key] || 'text',
				includeInExport: state.exportEdits?.[col.key] !== false,
				showInTableUI: wantsIt,
			});
			return;
		}

		if (!wantsIt && !existing) return;
		const finalLabel = label || col.defaultLabel;
		// only a changed label is checked, so a column that already clashes doesn't block unrelated saves
		const relabelProblem = existing?.label !== finalLabel ? getCustomColumnLabelProblem(finalLabel) : null;
		if (relabelProblem) problems.push(relabelProblem);
		customColumnsUpdate.push({
			key: col.key,
			label: finalLabel,
			source: existing?.source || 'custom',
			format: state.formatEdits?.[col.key] || existing?.format || 'text',
			includeInExport: state.exportEdits?.[col.key] ?? existing?.includeInExport !== false,
			showInTableUI: wantsIt,
		});
		if (existing?.label && existing.label !== finalLabel) {
			renames.push({ oldLabel: existing.label, newLabel: finalLabel });
		}
	});

	// #6405: every save passes through here, so this catches duplicates however they got into the list
	const duplicates = findDuplicateCustomColumnLabels({ transaction_custom_columns: customColumnsUpdate });
	duplicates.forEach((label) => problems.push(`"${label}" is used by two custom columns -- give one a different name.`));

	return { customColumnsUpdate: customColumnsUpdate.length ? customColumnsUpdate : null, renames, problems };
}
